import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global scroll-restoration. React Router doesn't reset scroll position
 * when you navigate between routes, so a teacher who scrolled to the
 * bottom of /generator and then clicked "Teacher Dashboard" would land
 * on the dashboard near the bottom of the page — feels like the link is
 * broken.
 *
 * Behaviour:
 * - Route change with a `#hash` → let the browser handle the anchor
 *   (e.g. `/generator#skills-generator` from the Tools menu).
 * - Route change without a hash → instant scroll to top.
 * - Hash-only changes (same pathname) are ignored so we don't fight
 *   in-page jumps the user triggered themselves.
 */
const ScrollToTop = () => {
  const { pathname, hash, search } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer one tick so the new route has actually mounted and the
      // anchor target exists in the DOM.
      const id = hash.replace(/^#/, "");
      const t = window.setTimeout(() => {
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo(0, 0);
        }
      }, 0);
      return () => window.clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash, search]);

  return null;
};

export default ScrollToTop;
